<template>
  <p class="item" v-show="status === 'errored' || status === 'empty'">
    <span v-if="status === 'empty'">{{ emptyMessage }}</span>
    <span v-if="status === 'errored'">{{ errorMessage }}</span>
  </p>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '~scss/variables';
</style>

<script>
// @flow
/**
 * A simple placeholder component that should be used when data lists are empty. It takes in a Promise, which should correspond to the list loader. The component inspects the Promise output and if it's empty - shows a message (empty message).
 * Alternatively, you can add a v-show to the component if there are some special conditions for it.
 *
 * If it errors out, a message can be passed for that (error-message).
 *
 * i.e. <Placy :promise="Promise" :empty-message="No stuff" :error-message="Oh snap!"/>

 * i.e. <Placy :promise="Promise" :empty-message="No stuff" :error-message="Oh snap!" v-show="crazyThings && mustBe && valid"/>
 *

 */

import { Observable } from 'rxjs'
import { map, catchError } from 'rxjs/operators'

export default {
  name: 'placy',

  data() {
    return {
      status: 'inactive'
    }
  },

  props: {
    observable: Observable,
    promise: Promise,
    emptyMessage: String,
    errorMessage: String
  },

  methods: {
    handleObservable(observable: Observable<Object>) {
      this.status = 'working'

      observable
        .pipe(
          map(res => {
            if (res && !res.length) {
              this.status = 'empty'
            } else {
              this.status = 'inactive'
            }
          }),
          catchError(err => {
            if (err && err.response && err.response.status === 404) {
              this.status = 'empty'
            } else {
              this.status = 'errored'
            }
            throw err
          })
        )
        .subscribe()
    },
    handlePromise(promise: Promise<Object>) {
      this.status = 'working'

      promise.then(res => {
        if (res && !res.length) {
          this.status = 'empty'
        } else {
          this.status = 'inactive'
        }
      }),
        catchError(err => {
          if (err && err.response && err.response.status === 404) {
            this.status = 'empty'
          } else {
            this.status = 'errored'
          }
          throw err
        })
    }
  },

  created() {
    if (this.observable) {
      this.handleObservable(this.observable)
    } else if (this.promise) {
      this.handlePromise(this.promise)
    }
  },

  watch: {
    observable: {
      handler(newObservable) {
        return this.handleObservable(newObservable)
      },
      deep: true
    },
    promise: {
      handler(newPromise) {
        return this.handleObservable(newPromise)
      },
      deep: true
    }
  }
}
</script>
